import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputNumber } from "primereact/inputnumber";
import { OverlayPanel } from "primereact/overlaypanel";
import { RadioButton } from "primereact/radiobutton";
import { Tooltip } from "primereact/tooltip";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Loading from "../../../../../../components/Loading/Loading";
import { useToastContext } from "../../../../../../contexts/ToastContext/ToastContext";
import useIsMobile from "../../../../../../hooks/useIsMobile";
import localeText from "../../../../../../locale";
import GetInfoService from "../../../../../../service/GetInfoService";
import IospediscoService from "../../../../../../service/iospedisco";
import { PLATFORM_PRODUCT_FEE } from "../../../../../../utilities/constants";
import { getCurrencySymbol } from "../../../../../../utilities/CurrencySwitch";
import { formatPrice } from "../../../../../../utilities/formatPrice";
import { localeISO } from "../../../../../../utilities/localeIso";
import { freightInputs, weightInputs } from "../../inputs";
import "./styles.css";

const formatValue = (value) => {
  return Number((Math.floor(value * 100) / 100).toFixed(2));
};

const ACCENT_COLOR = "#3B82F6";
const ACCENT_COLOR_BG = "#EFF6FF";

const plainInputStyle = {
  border: "none",
  boxShadow: "none",
  borderRadius: 0,
  borderBottom: "1px solid var(--surface-300)",
  paddingLeft: 0,
  paddingBottom: "0.5rem",
  backgroundColor: "transparent",
};

const modeOptions = [
  { value: 1, titleKey: "costCalcModeReceiveTitle", descKey: "costCalcModeReceiveDesc" },
  { value: 2, titleKey: "costCalcModeStoreTitle", descKey: "costCalcModeStoreDesc" },
];

const CostCalculator = ({
  isVisible,
  onHide,
  userCurrency,
  selectedCategory,
  selectedSubcategory,
  selectedTypeProduct,
  notHaveFreeSendCloud,
  percentMax: percentMaxProp,
  percentMin: percentMinProp,
  userInfo,
  currencyCode,
  setProductPrice,
  setProductCashbackPercentage,
  setProductWeight,
  setProductHeight,
  setProductWidth,
  setProductLength,
  initialPrice,
  initialCashbackPercent,
  initialHeight,
  initialWidth,
  initialLength,
  initialWeightKg,
  dialogBaseZIndex,
  setShippingIncluded,
  setProductFreightOption,
}) => {
  const downloadableId = parseInt(process.env.REACT_APP_DOWNLOADABLE_ID);

  const percentMin = useMemo(() => {
    const n = Number(percentMinProp);
    return Number.isFinite(n) && n >= 0 ? n : 1;
  }, [percentMinProp]);

  const percentMax = useMemo(() => {
    const n = Number(percentMaxProp);
    if (!Number.isFinite(n)) return Math.max(percentMin, 25);
    return n >= percentMin ? n : Math.max(percentMin, 25);
  }, [percentMaxProp, percentMin]);

  const [radioInput, setRadioInput] = useState(1);
  const [priceInput, setPriceInput] = useState(0);
  const [finalPrice, setFinalPrice] = useState(0);
  const [cashback, setCashback] = useState(() => {
    const n = Number(percentMinProp);
    return Number.isFinite(n) && n >= 0 ? n : 1;
  });
  const [weight, setWeight] = useState(0);
  const [freight, setFreight] = useState(false);
  const [freightPrice, setFreightPrice] = useState(null);
  const [isLoadingFreight, setIsLoadingFreight] = useState(false);
  const [isError, setIsError] = useState(false);
  const { isMobile } = useIsMobile();
  const { showErrorToast } = useToastContext();
  const [currencyForex, setCurrencyForex] = useState(1);
  const [height, setHeight] = useState(1);
  const [width, setWidth] = useState(1);
  const [length, setLength] = useState(1);

  const feeInfoRef = useRef(null);

  const styles = { width: isMobile ? "95vw" : "45rem" };

  const getInfoService = useMemo(() => new GetInfoService(), []);
  const iospediscoService = useMemo(() => new IospediscoService(), []);

  const resetCalculatorState = useCallback(() => {
    setRadioInput(1);
    setPriceInput(0);
    setFinalPrice(0);
    setCashback(percentMin);
    setWeight(0);
    setFreight(false);
    setFreightPrice(null);
    setIsLoadingFreight(false);
    setIsError(false);
    setHeight(1);
    setWidth(1);
    setLength(1);
  }, [percentMin]);

  const handleCloseCalculator = useCallback(() => {
    resetCalculatorState();
    onHide();
  }, [onHide, resetCalculatorState]);

  useEffect(() => {
    if (!isVisible) return;
    resetCalculatorState();
  }, [isVisible, resetCalculatorState]);

  useEffect(() => {
    if (!isVisible) resetCalculatorState();
  }, [isVisible, resetCalculatorState]);

  useEffect(() => {
    const getLocationId = async () => {
      try {
        setIsLoadingFreight(true);
        const res = await iospediscoService.getLocatiobId({
          token: localStorage.getItem("__extraconomy_token"),
          postal_code: userInfo?.postalCode,
          country: userInfo?.country,
        });

        const locationId =
          res?.locationResponse?.location_results[0]?.locations_list[0]?.id_location;

        if (locationId) {
          const compare = await iospediscoService.compare({
            token: localStorage.getItem("__extraconomy_token"),
            id_location: locationId,
            width: width,
            height: height,
            weight: weight,
            depth: length,
            pickup_date: userInfo?.amountDaysToPickUp,
          });

          const freight = compare?.comparisonResponse?.comparison_result;
          if (freight) {
            const freightValue = freight[0]?.comparison_items[0]?.total_amount_tax_excluded;
            if (currencyCode !== "EUR") {
              const resForex = await getInfoService.getForexRate("EUR", currencyCode);
              const converted =
                Number.isFinite(Number(resForex?.result)) && freightValue != null
                  ? Number(freightValue) * Number(resForex.result)
                  : Number(freightValue);
              setFreightPrice({ price: converted, currency: currencyCode });
            } else {
              setFreightPrice({ price: freightValue, currency: "EUR" });
            }
            setIsLoadingFreight(false);
            return;
          }

          if (
            compare?.comparisonResponseError?.comparison_result[0]?.failure_reasons[0]?.includes(
              "minimum volume is 100 cubic centimeters",
            )
          ) {
            showErrorToast({ detail: "minimunValueError" });
            setIsLoadingFreight(false);
          }
        }
      } catch (e) {
        console.log(e);
        setIsLoadingFreight(false);
      }
    };

    if (weight !== 0 && freight === 1) {
      getLocationId();
    }
  }, [
    weight,
    freight,
    showErrorToast,
    iospediscoService,
    userInfo,
    currencyCode,
    getInfoService,
    height,
    width,
    length,
  ]);

  const priceSafe = useMemo(() => {
    const n = Number(priceInput);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }, [priceInput]);

  const freightForexValue = useMemo(() => {
    const raw = freightPrice?.price;

    let n = 0;

    if (typeof raw === "number" && Number.isFinite(raw)) n = raw;
    else if (raw && typeof raw === "object" && Number.isFinite(Number(raw.price)))
      n = Number(raw.price);
    const fx = Number.isFinite(Number(currencyForex)) ? Number(currencyForex) : 1;

    return n * fx;
  }, [freightPrice, currencyForex]);

  const cashbackSafe = useMemo(() => {
    const n = Number(cashback);

    return Number.isFinite(n) ? n : percentMin;
  }, [cashback, percentMin]);

  const calcularPrecoCobrado = useCallback((price) => {
    const p = Number(price);

    const safe = Number.isFinite(p) && p >= 0 ? p : 0;

    setFinalPrice(safe + safe * PLATFORM_PRODUCT_FEE);
  }, []);

  const calcularValorAReceber = useCallback(
    (price) => {
      const p = Number(price);
      const safePrice = Number.isFinite(p) && p >= 0 ? p : 0;
      const cb = Number(cashback);
      const safeCb = Number.isFinite(cb) ? cb : percentMin;

      const taxaCalc = safePrice * PLATFORM_PRODUCT_FEE;
      const cashbackVal = safePrice * (safeCb / 100);
      const precoSemTaxaDeSeller = safePrice - taxaCalc;
      const precoSemCashback = precoSemTaxaDeSeller - cashbackVal - freightForexValue;

      setFinalPrice(precoSemCashback);
    },
    [cashback, freightForexValue, percentMin],
  );

  const applyPriceInProduct = useCallback(
    (priceToProduct) => {
      if (!Number.isFinite(priceToProduct) || priceToProduct < 0) {
        showErrorToast({ detail: "valueCanNotBeNegative" });
        return;
      }

      setProductPrice(priceToProduct);
      setProductCashbackPercentage(cashbackSafe);
      setProductWeight(weight);
      setProductHeight(height);
      setProductWidth(width);
      setProductLength(length);
      if (typeof setProductFreightOption === "function") {
        setProductFreightOption(Number(freight) || 0);
      }

      if (freightForexValue > 0) {
        setShippingIncluded(true);
      }

      onHide(finalPrice);
    },
    [
      finalPrice,
      cashbackSafe,
      setProductPrice,
      showErrorToast,
      onHide,
      setProductCashbackPercentage,
      setProductWeight,
      weight,
      setProductHeight,
      height,
      setProductWidth,
      width,
      setProductLength,
      length,
      freight,
      setProductFreightOption,
      freightForexValue,
      setShippingIncluded,
    ],
  );

  const taxa = priceSafe * PLATFORM_PRODUCT_FEE;

  const cashbackValue = priceSafe * (cashbackSafe / 100);

  const valorFinalRadio1Denominator = 0.95 - cashbackSafe / 100;
  const valorFinalRadio1 =
    Math.abs(valorFinalRadio1Denominator) < 1e-9
      ? 0
      : (priceSafe + freightForexValue) / valorFinalRadio1Denominator;

  const cashbackRadio1 = valorFinalRadio1 * (cashbackSafe / 100);

  const taxRadio1 = valorFinalRadio1 * 0.05;

  useEffect(() => {
    if (radioInput === 1) calcularPrecoCobrado(priceSafe);
    else calcularValorAReceber(priceSafe);
  }, [radioInput, calcularPrecoCobrado, calcularValorAReceber, priceSafe, cashback]);

  useEffect(() => {
    if (freight !== 1) {
      setFreightPrice(null);
      setWeight(0);
    }
  }, [freight, radioInput]);

  useEffect(() => {
    if (finalPrice < 0) setIsError(true);
    else setIsError(false);
  }, [finalPrice]);

  useEffect(() => {
    const getCurrencyForex = async () => {
      if (userCurrency !== "EUR") {
        const resForex = await getInfoService.getForexRate("EUR", userCurrency);
        const rate = Number(resForex?.result);
        setCurrencyForex(Number.isFinite(rate) && rate > 0 ? rate : 0);
      }
    };

    getCurrencyForex();
  }, [getInfoService, userCurrency]);

  const hasToCalculateFreight =
    selectedCategory &&
    selectedCategory.id !== downloadableId &&
    selectedSubcategory &&
    selectedTypeProduct !== 2 &&
    !notHaveFreeSendCloud;

  const summary = useMemo(() => {
    if (radioInput === 1) {
      return {
        finalStorePrice: formatValue(valorFinalRadio1),
        sellerFee: taxRadio1,
        buyerFee: taxRadio1,
        cashbackAmount: cashbackRadio1,
        netReceive: priceSafe,
      };
    }

    return {
      finalStorePrice: priceSafe,
      sellerFee: taxa,
      buyerFee: taxa,
      cashbackAmount: cashbackValue,
      netReceive: finalPrice,
    };
  }, [
    radioInput,
    valorFinalRadio1,
    taxRadio1,
    cashbackRadio1,
    priceSafe,
    taxa,
    cashbackValue,
    finalPrice,
  ]);

  const handleApply = () => {
    if (radioInput === 1) applyPriceInProduct(valorFinalRadio1);
    else applyPriceInProduct(priceSafe);
  };

  return (
    <Dialog
      showHeader={false}
      className="m-2 cost-calc-dialog"
      style={styles}
      breakpoints={{ "576px": "95vw" }}
      contentStyle={{ overflowX: "hidden" }}
      visible={isVisible}
      onHide={handleCloseCalculator}
      draggable={false}
      footer={
        <div className="grid-footer-actions mt-3">
          <Button
            label={localeText("costCalcBack")}
            outlined
            className="border-round-md m-0 text-sm font-medium"
            style={{
              backgroundColor: "var(--theme-background)",
              color: "var(--theme-secondary)",
              padding: "0.625rem 1rem",
            }}
            onClick={handleCloseCalculator}
          />
          <Button
            label={localeText("costCalcApplyButton")}
            className="border-round-md m-0 text-sm font-medium"
            style={{
              backgroundColor: "var(--theme-secondary)",
              color: "var(--theme-background)",
              padding: "0.625rem 1rem",
            }}
            onClick={handleApply}
            disabled={freight === 1 && (isLoadingFreight || weight === 0)}
          />
        </div>
      }
      {...(dialogBaseZIndex != null ? { baseZIndex: dialogBaseZIndex } : {})}
    >
      <div>
        <div className="flex align-items-start gap-2">
          <i
            className="pi pi-calculator flex-shrink-0"
            style={{ color: ACCENT_COLOR, fontSize: "1.1rem", marginTop: "0.2rem" }}
          ></i>
          <div style={{ minWidth: 0 }}>
            <p className="font-semibold text-lg m-0">{localeText("costCalcTitle")}</p>
            <p className="text-color-secondary text-sm mt-2 mb-0 line-height-3">
              {localeText("costCalcSubtitle")}
            </p>
          </div>
        </div>

        <hr
          className="my-3"
          style={{ border: "none", borderTop: "1px solid var(--surface-300)" }}
        />

        <div className="flex flex-column md:flex-row gap-3">
          {modeOptions.map((option) => {
            const selected = radioInput === option.value;
            return (
              <div
                key={option.value}
                role="radio"
                aria-checked={selected}
                tabIndex={0}
                onClick={() => !isLoadingFreight && setRadioInput(option.value)}
                onKeyDown={(e) => {
                  if ((e.key === "Enter" || e.key === " ") && !isLoadingFreight) {
                    e.preventDefault();
                    setRadioInput(option.value);
                  }
                }}
                className="flex-1 border-round-xl p-3"
                style={{
                  cursor: isLoadingFreight ? "not-allowed" : "pointer",
                  border: selected ? `1px solid ${ACCENT_COLOR}` : "1px solid var(--surface-300)",
                  backgroundColor: selected ? ACCENT_COLOR_BG : "transparent",
                  minWidth: 0,
                }}
              >
                <div className="flex justify-content-between align-items-start gap-2">
                  <div style={{ minWidth: 0 }}>
                    <p className="font-medium m-0 mb-1">{localeText(option.titleKey)}</p>
                    <p className="text-color-secondary text-sm m-0">{localeText(option.descKey)}</p>
                  </div>
                  <span
                    aria-hidden="true"
                    className="flex align-items-center justify-content-center flex-shrink-0"
                    style={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      border: `2px solid ${selected ? ACCENT_COLOR : "#ced4da"}`,
                      backgroundColor: "#fff",
                    }}
                  >
                    {selected && (
                      <span
                        style={{
                          width: "10px",
                          height: "10px",
                          borderRadius: "50%",
                          backgroundColor: ACCENT_COLOR,
                        }}
                      ></span>
                    )}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex flex-column md:flex-row gap-4 mt-4">
          <div className="flex-1 flex flex-column gap-2" style={{ minWidth: 0 }}>
            <label htmlFor="priceUniqueModal" className="text-sm font-medium">
              {radioInput === 1
                ? localeText("costCalcReceiveLabel")
                : localeText("costCalcStorePriceLabel")}
            </label>
            <InputNumber
              mode="currency"
              currency={userCurrency}
              locale={localeISO}
              className="w-full"
              inputClassName="text-xl font-semibold cost-calc-plain-input"
              inputStyle={plainInputStyle}
              name="priceUniqueModal"
              min={0}
              max={10000000}
              value={priceInput}
              disabled={isLoadingFreight}
              onChange={(e) => setPriceInput(e.value)}
            />
            <p className="text-color-secondary text-xs m-0">
              {radioInput === 1
                ? localeText("costCalcReceiveHelper")
                : localeText("costCalcStorePriceHelper")}
            </p>
          </div>

          <div className="flex-1 flex flex-column gap-2" style={{ minWidth: 0 }}>
            <label htmlFor="cashbackModal" className="text-sm font-medium">
              {localeText("costCalcCashbackLabel")}
            </label>
            <InputNumber
              className="w-full"
              inputClassName="text-xl font-semibold cost-calc-plain-input"
              inputStyle={plainInputStyle}
              name="cashbackModal"
              min={percentMin}
              max={percentMax}
              suffix="%"
              value={cashback}
              disabled={isLoadingFreight}
              onChange={(e) => setCashback(e.value)}
            />
            <p className="text-color-secondary text-xs m-0">
              {localeText("costCalcCashbackHelper")}
            </p>
          </div>
        </div>

        {freight === 1 && isLoadingFreight && (
          <div className="flex flex-row gap-2 align-items-center mt-3">
            <Loading classNameContainer={"my-0 text-left"} disableMargin />
            <p>{localeText("lookingTheBestShippingMethod")}</p>
          </div>
        )}

        {isError && <p className="text-red-400 text-sm">{localeText("valueCanNotBeNegative")}</p>}

        {hasToCalculateFreight && (
          <div
            className="border-round-xl p-3 mt-4"
            style={{ border: "1px solid var(--surface-300)" }}
          >
            <ul className="list-none mx-0 mt-0 p-0">
              <li>
                <label htmlFor="height">{localeText("height")} (cm)</label>
              </li>
              <li>
                <InputNumber
                  name="height"
                  className="my-2 w-full"
                  value={height}
                  min={1}
                  locale={localeISO}
                  disabled={isLoadingFreight}
                  onValueChange={(e) => {
                    if (e.value <= 0) {
                      showErrorToast({ detail: "dimensionsGreaterThanZero" });
                    } else setHeight(e.value);
                  }}
                  mode="decimal"
                  minFractionDigits={2}
                />
              </li>
              <li>
                <label htmlFor="width">{localeText("width")} (cm)</label>
              </li>
              <li>
                <InputNumber
                  name="width"
                  className="my-2 w-full"
                  value={width}
                  min={1}
                  locale={localeISO}
                  disabled={isLoadingFreight}
                  onValueChange={(e) => {
                    if (e.value <= 0) {
                      showErrorToast({ detail: "dimensionsGreaterThanZero" });
                    } else setWidth(e.value);
                  }}
                  mode="decimal"
                  minFractionDigits={2}
                />
              </li>
              <li>
                <label htmlFor="length">{localeText("length")} (cm)</label>
              </li>
              <li>
                <InputNumber
                  name="length"
                  className="my-2 w-full"
                  value={length}
                  min={1}
                  locale={localeISO}
                  disabled={isLoadingFreight}
                  onValueChange={(e) => {
                    if (e.value <= 0) {
                      showErrorToast({ detail: "dimensionsGreaterThanZero" });
                    } else setLength(e.value);
                  }}
                  mode="decimal"
                  minFractionDigits={2}
                />
              </li>
              <li className="mb-2">
                <label htmlFor="freight" className="mt-2">
                  {localeText("freight")}
                </label>
                <div className="mt-2 flex md:flex-row flex-column flex-wrap gap-3">
                  {freightInputs.map((freightInput, i) => {
                    if (notHaveFreeSendCloud && freightInput.label === "free") return null;
                    return (
                      <div key={i} className="flex align-items-center">
                        <RadioButton
                          inputId={`freightModal${i}`}
                          name={freightInput.name}
                          value={freightInput.value}
                          onChange={(e) => {
                            setFreight(e.value);
                            if (typeof setProductFreightOption === "function") {
                              setProductFreightOption(e.value);
                            }
                          }}
                          disabled={isLoadingFreight}
                          checked={freight === freightInput.value}
                        />
                        <label htmlFor={`freightModal${i}`} className="ml-2 cursor-pointer">
                          {localeText(freightInput.label)}{" "}
                          {freightInput.label === "free" && "(Sendcloud)"}
                          {freightInput.label === "free2" && `(${localeText("without")} Sendcloud)`}
                        </label>
                        <Tooltip target=".extracoin-tooltip" />
                        <i
                          className="pi pi-info-circle ml-1 extracoin-tooltip cursor-pointer text-600"
                          data-pr-tooltip={`${localeText(freightInput.explain)}`}
                          data-pr-position="right"
                          data-pr-at="right+5 top"
                          data-pr-my="left center-2"
                          tooltipoptions={{ event: "focus" }}
                        ></i>
                      </div>
                    );
                  })}
                </div>
              </li>
              {freight === 1 && (
                <>
                  <li>
                    <label htmlFor="weight" className="flex align-items-center flex-wrap gap-2">
                      {localeText("chooseWeightRange")} (kg){" "}
                      {isLoadingFreight && (
                        <Loading classNameContainer={"my-0 text-left"} disableMargin />
                      )}
                    </label>
                    {freight === 1 && weight === 0 && (
                      <p className="text-red-400 text-sm">
                        {localeText("chooseWeightRangeToCalculateShipping")}
                      </p>
                    )}
                  </li>
                  <li className="mt-2 flex md:flex-row flex-column flex-wrap gap-3">
                    {weightInputs.map((weightInput, i) => (
                      <div key={i} className="flex align-items-center">
                        <RadioButton
                          inputId={`weightModal${i}`}
                          name={weightInput.name}
                          value={weightInput.value}
                          disabled={isLoadingFreight}
                          onChange={(e) => setWeight(e.value)}
                          checked={weight === weightInput.value}
                        />
                        <label htmlFor={`weightModal${i}`} className="ml-2 cursor-pointer">
                          {weightInput.label}
                        </label>
                      </div>
                    ))}
                  </li>
                </>
              )}
            </ul>
          </div>
        )}

        <div className="border-round-xl p-3 mt-4" style={{ backgroundColor: "var(--surface-100)" }}>
          <p className="font-semibold m-0 mb-2">{localeText("costCalcSummaryTitle")}</p>

          <hr
            className="my-2"
            style={{ border: "none", borderTop: "1px solid var(--surface-300)" }}
          />

          <div className="flex justify-content-between align-items-center gap-2 py-1">
            <span className="text-sm">{localeText("costCalcFinalStorePrice")}</span>
            <span className="text-sm font-medium">
              {getCurrencySymbol(userCurrency)} {formatPrice(summary.finalStorePrice)}
            </span>
          </div>

          <div className="flex justify-content-between align-items-center gap-2 py-1">
            <span className="text-sm flex align-items-center gap-1">
              {localeText("costCalcSellerFee")}
              <i
                className="pi pi-info-circle cursor-pointer text-color-secondary"
                style={{ fontSize: "0.8rem" }}
                onClick={(e) => feeInfoRef.current?.toggle(e)}
              ></i>
            </span>
            <span className="text-sm font-medium">
              - {getCurrencySymbol(userCurrency)} {formatPrice(summary.sellerFee)}
            </span>
          </div>

          <OverlayPanel ref={feeInfoRef} dismissable>
            <p className="font-semibold m-0 mb-1">{localeText("costCalcFeePopoverTitle")}</p>
            <p className="text-sm text-color-secondary m-0" style={{ maxWidth: "260px" }}>
              {localeText("costCalcFeePopoverBody")}
            </p>
          </OverlayPanel>

          <div className="flex justify-content-between align-items-center gap-2 py-1">
            <span className="text-sm">{localeText("costCalcBuyerFee")}</span>
            <span className="text-sm font-medium">
              - {getCurrencySymbol(userCurrency)} {formatPrice(summary.buyerFee)}
            </span>
          </div>

          <div className="flex justify-content-between align-items-center gap-2 py-1">
            <span className="text-sm">
              {localeText("costCalcCashbackLine")} ({formatPrice(cashbackSafe)}%)
            </span>
            <span className="text-sm font-medium">
              - {getCurrencySymbol(userCurrency)} {formatPrice(summary.cashbackAmount)}
            </span>
          </div>

          {freightPrice && (
            <div className="flex justify-content-between align-items-center gap-2 py-1">
              <span className="text-sm">{localeText("freight")}</span>
              <span className="text-sm font-medium">
                - {getCurrencySymbol(userCurrency)} {formatPrice(freightForexValue)}
              </span>
            </div>
          )}

          <hr
            className="my-2"
            style={{ border: "none", borderTop: "1px solid var(--surface-300)" }}
          />

          <div
            className="flex justify-content-between align-items-center border-round-lg px-2 py-2"
            style={{ backgroundColor: "var(--green-50)" }}
          >
            <span className="text-sm font-semibold">{localeText("costCalcNetReceive")}</span>
            <span className="font-bold text-green-700">
              {getCurrencySymbol(userCurrency)} {formatPrice(summary.netReceive)}
            </span>
          </div>

          <p className="text-color-secondary text-xs mt-2 mb-0">
            {localeText("costCalcNetReceiveNote")}
          </p>
        </div>

        <p className="text-color-secondary text-sm mt-3 mb-0">{localeText("costCalcFooterNote")}</p>
      </div>
    </Dialog>
  );
};

export default CostCalculator;
